import type { IJobApplicationStore } from '../index';
import { trackUpdateStatusMultiple } from '../../../utils/events';
import type { Bucket } from '../types';
import { getDashisedBucket } from '../../../constants/jobApplicationStatus';
import {
  updateApplicationsStatus,
  STATUS_CHANGE_LOCATION,
} from '../../../graphql/hirer/updateApplicationsStatus';
import { sleep } from '../../../utils/sleep';
import { sendStatusUpdateCaptureEvent } from '../../../services/jobApplicationService/sendStatusUpdateCaptureEvent';
import type { IStore } from '../../Store';
import type { IJobApplication } from '../JobApplication';
import { MODAL_NAME } from 'src/constants/modals';
import type { ApplicationBucket } from 'src/App/AppPageBlock/graphql/hirer-types.generated';

const JOB_APP_COUNT_THRESHOLD_BEFORE_SLEEP = 200;

const sendTrackingEvents = (
  jobApplicationStore: IJobApplicationStore,
  newBucket: Bucket,
) => {
  const initialBucket = jobApplicationStore.bucket;
  trackUpdateStatusMultiple({
    ...jobApplicationStore.bulkMoveWithLimitEventAttributes,
    applicationStatus: getDashisedBucket(newBucket),
    bucket: getDashisedBucket(initialBucket),
    candidateCardStatus:
      jobApplicationStore.rootStore.getCandidateCardStateForTracking(),
  });
};

const postBulkMoveAction = async (
  jobApplicationStore: IJobApplicationStore,
  jobAppsToDeselect: IJobApplication[],
) => {
  // The underlying api call for updateStatusForProspects is not a synchronous call. This means that there may be a delay completing the update status action.
  // We observed that if there are >200 records, we don't need to sleep because the time Adcentre backend takes to do auxiliary tasks is long enough for the job app status to get to ElasticSearch cluster.
  if (jobAppsToDeselect.length <= JOB_APP_COUNT_THRESHOLD_BEFORE_SLEEP) {
    await sleep(2000);
  }
  jobApplicationStore.rootStore.uiStore.modalStore.removeModal(
    MODAL_NAME.BULK_ACTIONS_UPDATE_STATUS,
  );
  jobAppsToDeselect.forEach((jobApp) => jobApp.updateSelected(false));
  await jobApplicationStore.loadJobApplications();
};

export const updateStatusForSelectedJobApplications = async ({
  jobApplicationStore,
  store,
  bucket,
}: {
  jobApplicationStore: IJobApplicationStore;
  store: IStore;
  bucket: Bucket;
}): Promise<boolean> => {
  sendTrackingEvents(jobApplicationStore, bucket);

  const jobAppsToMove =
    jobApplicationStore.selectedJobApplicationUpToBulkMoveLimit;

  const success = await updateApplicationsStatus(
    jobApplicationStore.rootStore.positionId.toString(),
    bucket as ApplicationBucket,
    STATUS_CHANGE_LOCATION.CANDIDATE_LIST,
    jobAppsToMove.map((jobApplication) => ({
      prospectId: jobApplication.id,
      positionInList: jobApplication.positionInList,
    })),
  );

  if (success) {
    await Promise.all([
      sendStatusUpdateCaptureEvent({
        statusChangeLocation: 'Bulk',
        store,
        jobApplications: jobAppsToMove,
        destinationBucket: bucket,
      }),
      postBulkMoveAction(jobApplicationStore, jobAppsToMove),
    ]);
  }

  return success;
};
